import { DOCUMENT } from '@angular/common';
import { Component, DestroyRef, ElementRef, HostListener, Inject, OnDestroy, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs';
import { ROUTE_CONSTANTS } from '@app/core/constants/route.constant';
import { AuthApiService } from '@app/modules/auth/services/auth-api/auth-api.service';
import { IUserData } from '@app/modules/auth/interfaces/user-data.interface';
import { FIRST_SESSION_FEEDBACK_CLOSED_KEY } from '@app/modules/session-viewer/components/first-session-feedback/first-session-feedback.component';
import { isOnboardingStepDone, ONBOARDING_STEPS, onboardingStepProgress } from '../../constants/onboarding.constants';
import {
    IOnboardingStatus,
    IOnboardingStepDef,
    IOnboardingSteps,
    OnboardingTourStep,
} from '../../interfaces/onboarding.interface';
import { CookieConsentService } from '../../services/cookie-consent.service';
import { OnboardingService } from '../../services/onboarding.service';
import {
    ISidekickMessage,
    normalizeSidekickName,
    SIDEKICK_NAME_MAX_LENGTH,
    SidekickService,
    SIDEKICK_UNTARGETED_STEPS,
    SidekickTourStep,
} from '../../services/sidekick.service';
import { SIDEKICK_DOTS, SIDEKICK_GEOMETRY, SIDEKICK_SPARKS, SIDEKICK_TRAIL } from './sidekick-mark';

export type SidekickMood = 'idle' | 'attentive' | 'talking' | 'cheering';

/**
 * The sidekick puts itself together once for every coach, new or not: big in the middle of the start page
 * the dots gather from far away (`gather` → `assemble`), the disc grows in behind them and it comes to life with
 * a jump (`alive`), then it moves into its corner (`landing`) and the coach is on the start page.
 */
export type SidekickBirthPhase = 'none' | 'gather' | 'assemble' | 'alive' | 'naming' | 'named' | 'landing';
export const SIDEKICK_BIRTH_MS = { ASSEMBLE: 1_900, ALIVE: 1_600, NAMED: 1_600, LANDING: 900 } as const;
/** How much farther than in the intro the dots start out at birth. */
const BIRTH_SCATTER = 2.6;

/** How long a bubble stays open by itself; one with a button stays longer. */
export const SIDEKICK_BUBBLE_MS = 8_000;
export const SIDEKICK_BUBBLE_CTA_MS = 15_000;
/** The jump before a celebration bubble settles into talking. */
export const SIDEKICK_CHEER_MS = 1_200;
/** Pause after the page loads before the greeting or the next-step reminder, so the intro can finish. */
export const SIDEKICK_GREETING_DELAY_MS = 900;
/** The finished onboarding: how long the confetti flies (see the SCSS animation). */
export const SIDEKICK_CONFETTI_MS = 1_800;
/** …and when the logo's own dots, thrown out with it, fly back. */
const CONFETTI_RETURN_MS = 700;
const CONFETTI_PIECES = 36;

/**
 * The co-coach types its comments like on a typewriter: a few letters every step, done in at most
 * `SIDEKICK_TYPING_STEPS` steps (about 1.5 s), so a long tip does not keep the coach waiting.
 */
export const SIDEKICK_TYPING_STEP_MS = 24;
export const SIDEKICK_TYPING_STEPS = 60;

/** Intro: the dots fly in for this long (see the SCSS transition). */
const INTRO_START_MS = 30;

/** Sections without the sidekick: the paywall (it would compete with the plans), sign-in, legal and admin. */
const HIDDEN_SECTIONS: string[] = [
    ROUTE_CONSTANTS.UPGRADE,
    ROUTE_CONSTANTS.AUTH.ROOT,
    ROUTE_CONSTANTS.LEGAL.ROOT,
    ROUTE_CONSTANTS.ADMIN.ROOT,
];

/**
 * Where the speech bubble sits: above the disc, beside it (when above would cover an element marked
 * `data-sidekick-avoid`, e.g. the RUN button) or parked as a dot on the disc when both would.
 */
export type SidekickBubblePlacement = 'above' | 'beside' | 'parked' | 'lifted';
/** On `<body>` while a tour step runs: the step's name, so a page can show what is pointed at in full. */
export const SIDEKICK_TOUR_ATTRIBUTE = 'data-sidekick-tour';
/** Marks elements the speech bubble must never cover. */
export const SIDEKICK_AVOID_SELECTOR = '[data-sidekick-avoid]';
/** Gap between the bubble and the disc, and the bubble's narrowest useful width beside it. */
const BUBBLE_GAP = 12;
const BUBBLE_WIDTH = 280;
const BUBBLE_MIN_BESIDE = 180;
const SCREEN_EDGE = 16;

/** Below this width the sidekick lays out for phones (see the SCSS breakpoint). */
const MOBILE_MAX_WIDTH = 768;
/** On a phone, a field of the tour scrolls this far below the top (room for the app's header). */
const MOBILE_TOUR_TOP = 80;

/** Room around a field the guided first session points at. */
const SPOTLIGHT_PADDING = 8;
/** Re-measure the spotlight once the smooth scroll and opening animations are done. */
const SPOTLIGHT_SETTLE_MS = 450;

/** Where the guided first session points: the field's box on screen, padding included. */
export interface ISidekickSpotlight {
    top: number;
    left: number;
    width: number;
    height: number;
}

/** Class on `<body>` while the sidekick is shown. */
export const SIDEKICK_BODY_CLASS = 'has-sidekick';

/**
 * The sidekick in the bottom-right corner: the Saideline logo mark that comes to life.
 * It blinks its motion lines, leans in while a session is generated, jumps after a session
 * and talks in a small speech bubble. Coaches can mute its tips or hide it for the visit.
 *
 * It also tells the getting-started checklist: a badge on the disc shows the progress, the disc
 * opens the steps, once per visit it reminds the coach of the next step and it celebrates every
 * finished step. Closing a bubble or the steps never ends the checklist; finishing it does.
 */
@Component({
    selector: 'app-sidekick',
    templateUrl: './sidekick.component.html',
    styleUrl: './sidekick.component.scss',
    standalone: false,
})
export class SidekickComponent implements OnDestroy {
    readonly disc = SIDEKICK_GEOMETRY.DISC;
    readonly markTransform = `translate(${SIDEKICK_GEOMETRY.OFFSET_X} ${SIDEKICK_GEOMETRY.OFFSET_Y})`;
    readonly radius = SIDEKICK_GEOMETRY.RADIUS;
    readonly motionDots = SIDEKICK_DOTS.filter((dot) => dot.motion).map((dot, index) => this.withIntro(dot, index));
    readonly playerDots = SIDEKICK_DOTS.filter((dot) => !dot.motion).map((dot, index) => this.withIntro(dot, index));
    readonly trail = SIDEKICK_TRAIL;
    readonly sparks = SIDEKICK_SPARKS;
    readonly steps = ONBOARDING_STEPS;

    readonly nameMaxLength = SIDEKICK_NAME_MAX_LENGTH;

    message: ISidekickMessage | null = null;
    /** The open comment in full (translated): read out by screen readers, and it sets the bubble's size. */
    fullText = '';
    /** The part of the comment typed so far. */
    typedText = '';
    private typingTimer?: ReturnType<typeof setTimeout>;
    /**
     * Confetti from the disc when the onboarding is done: the logo's dots in lime (some ink), thrown
     * out over the page and caught again. Fixed directions, so it looks the same every time.
     */
    readonly confetti = Array.from({ length: CONFETTI_PIECES }, (_, index) => {
        // Up and to the left: the disc sits in the bottom-right corner
        const angle = Math.PI * (0.45 + (0.6 * index) / (CONFETTI_PIECES - 1));
        const distance = 140 + ((index * 97) % 220);
        return {
            dx: `${(Math.cos(angle) * distance).toFixed(0)}px`,
            dy: `${(-Math.sin(angle) * distance).toFixed(0)}px`,
            spin: `${((index * 137) % 360) - 180}deg`,
            delay: `${(index * 23) % 180}ms`,
            size: index % 3 === 0 ? 10 : 7,
            isInk: index % 5 === 0,
            isSquare: index % 2 === 0,
        };
    });
    /** Where the confetti starts: the middle of the disc, in the viewport. */
    confettiOrigin: { x: number; y: number } | null = null;
    private confettiTimers: ReturnType<typeof setTimeout>[] = [];
    bubblePlacement: SidekickBubblePlacement = 'above';
    /** How far a lifted tour bubble moves up, so it sits above the spotlighted element. */
    bubbleLift = 0;
    /** The coach opened a parked bubble from the disc: show it even if it covers something. */
    private isBubbleForced = false;
    private placementFrame?: number;

    /** The name panel in the corner is open (rename, or naming after the prompt). */
    isNameOpen = false;
    nameDraft = '';
    nameError: 'invalid' | 'failed' | null = null;
    isSavingName = false;
    /** The message's params, translated. */
    messageParams: Record<string, string> = {};

    /** CTA label: translated, unless the page handed over ready-made text. */
    get ctaLabel(): string {
        const label = this.message?.cta?.label ?? '';
        return this.message?.plain ? label : (this.translate.instant(label, this.messageParams) as string);
    }
    isMenuOpen = false;
    /** After the tour through the first session the checklist stays open, so the coach follows it on. */
    isFollowingChecklist = false;
    /** The tour through the first session is over: once the co-coach stops talking, the checklist follows. */
    private isChecklistNext = false;
    isMuted = false;
    /** The dots are still at their intro start positions. */
    isScattered = false;
    birthPhase: SidekickBirthPhase = 'none';
    /** Step of the guided first session: the rest of the page fades behind the checklist or the field. */
    tourStep: SidekickTourStep | null = null;
    /** Page the tour in progress runs on; leaving it ends the tour. */
    private tourPath: string | null = null;
    spotlight: ISidekickSpotlight | null = null;
    private spotlightTimer?: ReturnType<typeof setTimeout>;

    private user: IUserData | null = null;
    private sessionCount: number | null = null;
    private isHidden = false;
    private isBusy = false;
    private isHovered = false;
    private isCheering = false;
    private isOnAllowedPage = true;
    private isOnHome = false;
    private isConsentDecided = false;
    private bubbleTimer?: ReturnType<typeof setTimeout>;
    private cheerTimer?: ReturnType<typeof setTimeout>;
    private greetingTimer?: ReturnType<typeof setTimeout>;
    private birthTimers: ReturnType<typeof setTimeout>[] = [];
    private wasVisible = false;
    /** The sidekick appeared before the account's status was known: its entrance waits for it. */
    private isAwaitingStatus = false;
    private onboarding: IOnboardingStatus | null = null;

    constructor(
        readonly sidekick: SidekickService,
        private readonly authService: AuthApiService,
        private readonly router: Router,
        private readonly host: ElementRef<HTMLElement>,
        private readonly cookieConsent: CookieConsentService,
        private readonly onboardingService: OnboardingService,
        private readonly translate: TranslateService,
        @Inject(DOCUMENT) private readonly document: Document
    ) {
        const destroyRef = inject(DestroyRef);
        this.updateRoute(router.url);

        if (!sidekick.enabled) {
            return;
        }

        // Before `user$` gets the coach, so a test account starts over from this sign-in
        authService.signedIn$.pipe(takeUntilDestroyed(destroyRef)).subscribe((user) => this.onSignIn(user));
        authService.user$.pipe(takeUntilDestroyed(destroyRef)).subscribe((user) => this.onUser(user));
        onboardingService.status$.pipe(takeUntilDestroyed(destroyRef)).subscribe((status) => this.onOnboarding(status));

        router.events
            .pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                takeUntilDestroyed(destroyRef)
            )
            .subscribe((event) => {
                this.updateRoute(event.urlAfterRedirects);
                if (this.tourPath !== null && this.tourPath !== this.pathOf(event.urlAfterRedirects)) {
                    // A tour lives on its page: the guided first session on the training form, the
                    // tour through the first session on the session page
                    this.sidekick.endTour();
                }
                this.isMenuOpen = false;
                this.isFollowingChecklist = false;
                this.sidekick.setBusy(false);
                this.scheduleGreeting();
                this.syncVisibility();
            });

        sidekick.progress$.pipe(takeUntilDestroyed(destroyRef)).subscribe((step) => this.saveProgress(step));
        sidekick.sessionTourDone$.pipe(takeUntilDestroyed(destroyRef)).subscribe(() => {
            // After this turn: the celebration comes first, the checklist waits until it is closed
            this.isChecklistNext = true;
            setTimeout(() => this.followChecklist());
        });
        sidekick.seen$.pipe(takeUntilDestroyed(destroyRef)).subscribe((event) => onboardingService.markSidekickSeen(event));
        sidekick.message$.pipe(takeUntilDestroyed(destroyRef)).subscribe((message) => this.onMessage(message));
        sidekick.tourStep$.pipe(takeUntilDestroyed(destroyRef)).subscribe((step) => this.onTourStep(step));
        sidekick.busy$.pipe(takeUntilDestroyed(destroyRef)).subscribe((busy) => (this.isBusy = busy));
        sidekick.muted$.pipe(takeUntilDestroyed(destroyRef)).subscribe((muted) => (this.isMuted = muted));
        sidekick.hidden$.pipe(takeUntilDestroyed(destroyRef)).subscribe((hidden) => {
            this.isHidden = hidden;
            this.syncVisibility();
        });
        // Wait until the cookie banner is gone: it covers the corner, and a first visit starts with it.
        cookieConsent.decided$.pipe(takeUntilDestroyed(destroyRef)).subscribe((decided) => {
            this.isConsentDecided = decided;
            this.syncVisibility();
            this.scheduleGreeting();
        });
    }

    get isVisible(): boolean {
        return this.sidekick.enabled && !!this.user && !this.isHidden && this.isOnAllowedPage && this.isConsentDecided;
    }

    /** The getting-started checklist still has open steps. */
    get hasOpenSteps(): boolean {
        return !!this.onboarding && !this.onboarding.dismissed && this.doneCount < this.steps.length;
    }

    get doneCount(): number {
        return this.steps.filter((step) => this.isDone(step)).length;
    }

    /** Share of finished steps, 0–100, for the progress bar. */
    get progress(): number {
        return Math.round((this.doneCount / this.steps.length) * 100);
    }

    /** The step to do next: highlighted in the checklist, a tap away (e.g. the squad after the first session). */
    get nextOpenStep(): IOnboardingStepDef | null {
        return this.nextStep();
    }

    /** "3/16" next to the squad step while the squad is not complete. */
    stepProgress(step: IOnboardingStepDef): string | null {
        return onboardingStepProgress(this.onboarding, step);
    }

    isDone(step: IOnboardingStepDef): boolean {
        return isOnboardingStepDone(this.onboarding, step);
    }

    /**
     * The checklist is open: bright in front of the darkened page at the start of the tour (the co-coach's
     * words then sit in it), or from the disc and after the tour through the first session — but only while
     * the co-coach says nothing, so its comment and the checklist never show at the same time.
     */
    get isChecklistOpen(): boolean {
        if (!this.hasOpenSteps) {
            return false;
        }
        if (this.tourStep === 'checklist') {
            return true;
        }
        return !this.message && (this.isMenuOpen || this.isFollowingChecklist);
    }

    /** The speech bubble; at the checklist step of the tour its words are shown in the checklist instead. */
    get isBubbleShown(): boolean {
        return !!this.message && this.tourStep !== 'checklist';
    }

    get mood(): SidekickMood {
        if (this.isCheering) {
            return 'cheering';
        }
        if (this.message) {
            return 'talking';
        }
        if (this.isBusy || this.isHovered || this.isMenuOpen) {
            return 'attentive';
        }
        return 'idle';
    }

    get isBeingBorn(): boolean {
        return this.birthPhase !== 'none';
    }

    /** The name the coach gave their co-coach; null until named. */
    get coachName(): string | null {
        return this.user?.sidekickName ?? null;
    }

    toggleMenu(): void {
        if (this.isBeingBorn) {
            this.skipBirth();
            return;
        }
        if (this.message && this.bubblePlacement === 'parked') {
            // A parked bubble opens from the disc, even where it covers something
            this.isBubbleForced = true;
            this.bubblePlacement = 'above';
            this.startBubbleTimer(this.message);
            return;
        }
        this.isNameOpen = false;
        this.isFollowingChecklist = false;
        this.isMenuOpen = !this.isMenuOpen;
        if (this.isMenuOpen) {
            this.sidekick.closeMessage('timeout');
        }
    }

    closeMenu(): void {
        this.isMenuOpen = false;
        this.isFollowingChecklist = false;
    }

    toggleMuted(): void {
        this.sidekick.toggleMuted();
        this.isMenuOpen = false;
    }

    hide(): void {
        this.isMenuOpen = false;
        this.sidekick.hideForNow();
    }

    closeMessage(): void {
        this.sidekick.closeMessage('close');
    }

    /** Still typing the comment. */
    get isTyping(): boolean {
        return this.typedText.length < this.fullText.length;
    }

    onCta(): void {
        const action = this.message?.cta?.action;
        if (action === 'external') {
            this.sidekick.closeMessage('cta');
            this.sidekick.runExternalAction();
            return;
        }
        if (action === 'tourNext') {
            this.sidekick.nextTourStep();
            return;
        }
        if (this.message?.cta?.route) {
            // The coach follows the link: no checklist on top of the next page
            this.isChecklistNext = false;
        }
        this.sidekick.closeMessage('cta');
        if (action === 'celebrationNext') {
            this.sidekick.continueCelebration();
            return;
        }
        if (action === 'name') {
            this.openNamePanel();
        }
    }

    /** Opens the name panel in the corner: to give the co-coach a name or a new one. */
    openNamePanel(): void {
        this.isMenuOpen = false;
        this.sidekick.closeMessage('timeout');
        this.nameDraft = this.coachName ?? '';
        this.nameError = null;
        this.isNameOpen = true;
    }

    closeNamePanel(): void {
        if (!this.coachName) {
            this.sidekick.skipNaming();
        }
        this.isNameOpen = false;
        this.nameError = null;
        this.scheduleGreeting();
    }

    onNameInput(value: string): void {
        this.nameDraft = value;
        this.nameError = null;
    }

    /** Saves the name, at birth or from the corner panel. */
    saveName(): void {
        const name = normalizeSidekickName(this.nameDraft);
        if (!name) {
            this.nameError = 'invalid';
            return;
        }
        if (this.isSavingName) {
            return;
        }
        if (name === this.coachName) {
            this.isNameOpen = false;
            return;
        }
        const isRename = !!this.coachName;
        const isAtBirth = this.birthPhase === 'naming';
        this.isSavingName = true;
        this.nameError = null;
        this.onboardingService.nameSidekick(name).subscribe({
            next: ({ sidekickName }) => {
                this.isSavingName = false;
                this.authService.updateSidekickName(sidekickName);
                if (isAtBirth) {
                    this.sidekick.confirmName(sidekickName, false);
                    this.introduceName();
                } else {
                    this.isNameOpen = false;
                    this.sidekick.confirmName(sidekickName, isRename);
                    // Named before the guided session: it starts now
                    this.scheduleGreeting();
                }
            },
            error: () => {
                this.isSavingName = false;
                this.nameError = 'failed';
            },
        });
    }

    /** "Later" at birth: the sidekick lands without a name; the menu still offers to name it. */
    skipNaming(): void {
        if (this.birthPhase !== 'naming') {
            return;
        }
        this.sidekick.skipNaming();
        this.land();
    }

    /** A click on the sidekick or Escape during the birth jumps straight to the landing. */
    skipBirth(): void {
        if (this.birthPhase === 'naming') {
            // Naming waits for the coach; Escape means "later", a click on the disc does nothing
            return;
        }
        if (this.birthPhase === 'none' || this.birthPhase === 'landing') {
            return;
        }
        this.clearBirthTimers();
        this.isScattered = false;
        // Skipping the animation never skips the name: without one it asks right away
        this.afterAlive();
    }

    setHovered(isHovered: boolean): void {
        this.isHovered = isHovered;
    }

    /** Hovering the bubble keeps it open; leaving restarts its timer. */
    pauseBubble(isPaused: boolean): void {
        clearTimeout(this.bubbleTimer);
        if (!isPaused && this.message && this.bubblePlacement !== 'parked') {
            this.startBubbleTimer(this.message);
        }
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        this.isMenuOpen = false;
        this.sidekick.endTour();
        if (this.birthPhase === 'naming') {
            this.skipNaming();
        } else if (this.isNameOpen) {
            this.closeNamePanel();
        }
        this.skipBirth();
    }

    @HostListener('window:resize')
    @HostListener('window:scroll')
    onViewportChange(): void {
        if (this.message) {
            this.schedulePlacement();
        }
        if (this.spotlight) {
            this.measureSpotlight();
        }
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if ((this.isMenuOpen || this.isFollowingChecklist) && !this.host.nativeElement.contains(event.target as Node)) {
            this.isMenuOpen = false;
            this.isFollowingChecklist = false;
        }
    }

    ngOnDestroy(): void {
        if (this.placementFrame !== undefined) {
            cancelAnimationFrame(this.placementFrame);
        }
        clearTimeout(this.bubbleTimer);
        clearTimeout(this.cheerTimer);
        clearTimeout(this.greetingTimer);
        clearTimeout(this.typingTimer);
        clearTimeout(this.spotlightTimer);
        this.confettiTimers.forEach((timer) => clearTimeout(timer));
        this.clearBirthTimers();
        this.document.body.classList.remove(SIDEKICK_BODY_CLASS);
        this.document.body.removeAttribute(SIDEKICK_TOUR_ATTRIBUTE);
    }

    /** A test account starts over as a new coach: also on this device, incl. the first-session question. */
    private onSignIn(user: IUserData): void {
        if (!user.testAccount) {
            return;
        }
        this.sidekick.forgetCoach(user.sub);
        try {
            Object.keys(localStorage)
                .filter((key) => key.startsWith(`${FIRST_SESSION_FEEDBACK_CLOSED_KEY}.${user.sub}`))
                .forEach((key) => localStorage.removeItem(key));
        } catch {
            // Storage blocked: nothing remembered to forget
        }
    }

    private onUser(user: IUserData | null): void {
        const isSameCoach = !!user && user.sub === this.user?.sub;
        const total = user?.stats?.totalSessions;
        const count = user && typeof total === 'number' ? total : null;

        this.sidekick.setUser(user?.sub ?? null, user?.onboardingTourStep);

        // A generated session raises the count (X-Updated-Stats): celebrate the first three.
        if (isSameCoach && count !== null && this.sessionCount !== null && count > this.sessionCount) {
            this.sidekick.celebrateSession(count, this.hasOpenSteps ? this.nextStep('sessionCreated') : null);
            this.onboardingService.refreshIfOpen('sessionCreated');
        }

        this.user = user;
        this.sessionCount = count;
        this.syncVisibility();
        this.scheduleGreeting();
    }

    /**
     * Every coach on the start page is guided through a session once; a coach without any session
     * gets a hello, once. Otherwise, while the checklist has open steps, the sidekick names the next
     * one, once per visit.
     */
    private scheduleGreeting(): void {
        clearTimeout(this.greetingTimer);
        if (!this.isVisible || !(this.shouldOnboard() || this.hasOpenSteps || !this.coachName)) {
            return;
        }
        this.greetingTimer = setTimeout(() => {
            if (!this.isVisible || this.isBeingBorn || this.message || this.isMenuOpen || this.isNameOpen || this.isBusy) {
                return;
            }
            // The guided session starts with a name: a co-coach that missed its birth (e.g. on another
            // device) asks for one first, unless the coach said "later"
            if (
                this.shouldOnboard() &&
                this.onboarding &&
                this.isTourAhead &&
                !this.coachName &&
                this.sidekick.mayAskForName
            ) {
                this.openNamePanel();
                return;
            }
            // Every coach is guided through a session once, also those who created sessions before;
            // the checklist shows once its status is known
            if (this.shouldOnboard() && this.onboarding && this.sidekick.startTour(this.hasOpenSteps)) {
                return;
            }
            if (this.shouldGreet() && this.sidekick.greetFirstVisit()) {
                return;
            }
            // From now on the sidekick is the coach's own co-coach: one without a name asks for one, once
            if (!this.coachName && this.sidekick.askForName()) {
                return;
            }
            const next = this.hasOpenSteps ? this.nextStep() : null;
            if (next) {
                this.sidekick.remindNextStep(next, !this.isOnRoute(next.route));
            }
        }, SIDEKICK_GREETING_DELAY_MS);
    }

    /** The birth is over and the guided session is still to come. */
    private get isTourAhead(): boolean {
        const step = this.sidekick.onboardingStep;
        return step !== 'birth' && step !== 'done';
    }

    /** Saves the onboarding step on the coach's account, so an unfinished onboarding goes on from there. */
    private saveProgress(step: OnboardingTourStep): void {
        this.authService.updateOnboardingTourStep(step);
        this.onboardingService.saveTourStep(step).subscribe({
            next: ({ onboardingTourStep }) => this.authService.updateOnboardingTourStep(onboardingTourStep),
            error: () => {},
        });
    }

    private shouldGreet(): boolean {
        return this.isOnHome && this.sessionCount === 0;
    }

    /** The birth and the guided session play once for every coach on the start page, sessions or not. */
    private shouldOnboard(): boolean {
        return this.isOnHome && this.sessionCount !== null;
    }

    /** Ticks off and celebrates the steps the coach just finished. */
    private onOnboarding(status: IOnboardingStatus | null): void {
        const previous = this.onboarding;
        this.onboarding = status;

        if (status && this.user) {
            // What the account knows wins over this device's cache: another device may have gone on
            const step = this.sidekick.syncAccount(status.tourStep, status.sidekickSeen);
            this.authService.updateOnboardingTourStep(step);
        }
        if (status && this.isAwaitingStatus) {
            this.isAwaitingStatus = false;
            this.playEntrance();
        }

        if (previous && status && !status.dismissed) {
            const finished = this.steps.filter(
                (step) => isOnboardingStepDone(status, step) && !isOnboardingStepDone(previous, step)
            );
            // The first session has its own celebration (see onUser); the last step always ends the story.
            const done =
                finished.filter((step) => step.key !== 'sessionCreated').pop() ??
                (!this.hasOpenSteps ? finished.pop() : undefined);
            if (done) {
                this.sidekick.celebrateStep(done, this.nextStep());
            }
        }
        this.scheduleGreeting();
    }

    /** The first open step in checklist order, optionally treating `alsoDone` as finished. */
    private nextStep(alsoDone?: keyof IOnboardingSteps): IOnboardingStepDef | null {
        return this.steps.find((step) => step.key !== alsoDone && !this.isDone(step)) ?? null;
    }

    private isOnRoute(route: string[]): boolean {
        return this.router.isActive(this.router.createUrlTree(route), {
            paths: 'exact',
            queryParams: 'ignored',
            fragment: 'ignored',
            matrixParams: 'ignored',
        });
    }

    /** Points at the field of this step, or skips a step whose field isn't on the page. */
    private onTourStep(step: SidekickTourStep | null): void {
        this.tourStep = step;
        if (step) {
            this.document.body.setAttribute(SIDEKICK_TOUR_ATTRIBUTE, step);
        } else {
            this.document.body.removeAttribute(SIDEKICK_TOUR_ATTRIBUTE);
        }
        this.tourPath = step ? (this.tourPath ?? this.pathOf(this.router.url)) : null;
        this.spotlight = null;
        clearTimeout(this.spotlightTimer);
        if (!step || SIDEKICK_UNTARGETED_STEPS.includes(step)) {
            return;
        }
        this.isMenuOpen = false;
        // After the page has reacted to the step (the comment field opens)
        setTimeout(() => {
            const target = this.tourTarget();
            if (this.tourStep !== step) {
                return;
            }
            if (!target) {
                this.sidekick.nextTourStep();
                return;
            }
            this.scrollToTarget(target);
            this.measureSpotlight();
            this.spotlightTimer = setTimeout(() => this.measureSpotlight(), SPOTLIGHT_SETTLE_MS);
        });
    }

    /**
     * Brings the field of a tour step into view. On a phone it goes to the top of the screen: the bubble
     * and the disc take the bottom, and centered they would cover it.
     */
    private scrollToTarget(target: HTMLElement): void {
        const behavior: ScrollBehavior = this.prefersReducedMotion() ? 'auto' : 'smooth';
        const view = this.document.defaultView;
        if (view && view.innerWidth < MOBILE_MAX_WIDTH) {
            const top = target.getBoundingClientRect().top + view.scrollY - MOBILE_TOUR_TOP;
            view.scrollTo?.({ top: Math.max(top, 0), behavior });
            return;
        }
        target.scrollIntoView?.({ behavior, block: 'center' });
    }

    private pathOf(url: string): string {
        return url.split(/[?#]/)[0];
    }

    private tourTarget(): HTMLElement | null {
        return this.tourStep ? this.document.querySelector<HTMLElement>(`[data-tour="${this.tourStep}"]`) : null;
    }

    measureSpotlight(): void {
        const rect = this.tourTarget()?.getBoundingClientRect();
        this.spotlight =
            rect && rect.width > 0 && rect.height > 0
                ? {
                      top: rect.top - SPOTLIGHT_PADDING,
                      left: rect.left - SPOTLIGHT_PADDING,
                      width: rect.width + 2 * SPOTLIGHT_PADDING,
                      height: rect.height + 2 * SPOTLIGHT_PADDING,
                  }
                : null;
        if (this.message) {
            this.schedulePlacement();
        }
    }

    private onMessage(message: ISidekickMessage | null): void {
        this.message = message;
        this.messageParams = Object.fromEntries(
            Object.entries(message?.params ?? {}).map(([name, key]) => [
                name,
                message?.plain ? key : (this.translate.instant(key) as string),
            ])
        );
        clearTimeout(this.bubbleTimer);
        clearTimeout(this.cheerTimer);
        this.isCheering = false;
        this.isBubbleForced = false;
        this.bubblePlacement = 'above';
        this.startTyping(message ? (this.translate.instant(message.text, this.messageParams) as string) : '');

        if (!message) {
            if (this.isChecklistNext) {
                // After this turn: another tip may follow right away, the checklist waits for it
                setTimeout(() => this.followChecklist());
            }
            return;
        }

        this.isMenuOpen = false;
        this.isNameOpen = false;
        this.schedulePlacement();
        if (message.event === 'tourComplete' && !this.prefersReducedMotion()) {
            this.throwConfetti();
        }
        if (message.cheer && !this.prefersReducedMotion()) {
            this.isCheering = true;
            this.cheerTimer = setTimeout(() => (this.isCheering = false), SIDEKICK_CHEER_MS);
        }
        this.startBubbleTimer(message);
    }

    /** Types the comment letter by letter; at once where the coach prefers less motion. */
    private startTyping(text: string): void {
        clearTimeout(this.typingTimer);
        this.fullText = text;
        if (!text || this.prefersReducedMotion()) {
            this.typedText = text;
            return;
        }
        this.typedText = '';
        const lettersPerStep = Math.max(1, Math.ceil(text.length / SIDEKICK_TYPING_STEPS));
        const type = (): void => {
            this.typedText = text.slice(0, this.typedText.length + lettersPerStep);
            if (this.isTyping) {
                this.typingTimer = setTimeout(type, SIDEKICK_TYPING_STEP_MS);
            }
        };
        this.typingTimer = setTimeout(type, SIDEKICK_TYPING_STEP_MS);
    }

    /** The tour through the first session is over and the co-coach is quiet: the checklist shows the way on. */
    private followChecklist(): void {
        if (!this.isChecklistNext || this.message || this.tourStep) {
            return;
        }
        this.isChecklistNext = false;
        this.isFollowingChecklist = this.hasOpenSteps && this.isVisible;
    }

    /**
     * The finished onboarding: the sidekick throws its dots — the logo's own fly apart and back, and
     * confetti flies from the disc over the page and returns to it.
     */
    private throwConfetti(): void {
        const disc = this.host.nativeElement.querySelector<HTMLElement>('.sidekick__disc')?.getBoundingClientRect();
        if (!disc) {
            return;
        }
        this.confettiTimers.forEach((timer) => clearTimeout(timer));
        this.confettiOrigin = { x: disc.left + disc.width / 2, y: disc.top + disc.height / 2 };
        this.isScattered = true;
        this.confettiTimers = [
            setTimeout(() => (this.isScattered = false), CONFETTI_RETURN_MS),
            setTimeout(() => (this.confettiOrigin = null), SIDEKICK_CONFETTI_MS),
        ];
    }

    /** Measures after the bubble is drawn, so it knows its size. */
    private schedulePlacement(): void {
        if (this.placementFrame !== undefined) {
            cancelAnimationFrame(this.placementFrame);
        }
        this.placementFrame = requestAnimationFrame(() => {
            this.placementFrame = undefined;
            this.placeBubble();
        });
    }

    /**
     * Keeps the bubble off the elements marked `data-sidekick-avoid` (the RUN button): above the
     * disc, else beside it, else parked as a dot on the disc until the coach scrolls or taps it.
     */
    placeBubble(): void {
        const bubble = this.host.nativeElement.querySelector<HTMLElement>('.sidekick__bubble');
        const disc = this.host.nativeElement.querySelector<HTMLElement>('.sidekick__disc');
        if (!this.message || !bubble || !disc || this.isBubbleForced) {
            return;
        }
        const spot = this.message.sticky ? this.spotlight : null;
        const avoid = [
            ...Array.from(this.document.querySelectorAll<HTMLElement>(SIDEKICK_AVOID_SELECTOR)).map((element) =>
                element.getBoundingClientRect()
            ),
            // A tour step never covers what it points at
            ...(spot ? [{ ...spot, right: spot.left + spot.width, bottom: spot.top + spot.height }] : []),
        ].filter((rect) => rect.width > 0 && rect.height > 0);
        const previous = this.bubblePlacement;
        let placement: SidekickBubblePlacement = 'above';
        this.bubbleLift = 0;

        if (avoid.length) {
            const viewport = this.document.defaultView?.innerWidth ?? BUBBLE_WIDTH + 2 * SCREEN_EDGE;
            const d = disc.getBoundingClientRect();
            const current = bubble.getBoundingClientRect();
            // The text reflows with the width: estimate each placement's height from the current one
            const area = current.width * current.height;
            const aboveWidth = Math.min(BUBBLE_WIDTH, viewport - 2 * SCREEN_EDGE);
            const besideWidth = Math.min(BUBBLE_WIDTH, d.left - BUBBLE_GAP - SCREEN_EDGE);
            const above = {
                left: d.right - aboveWidth,
                right: d.right,
                top: d.top - BUBBLE_GAP - area / aboveWidth,
                bottom: d.top - BUBBLE_GAP,
            };
            const beside = {
                left: d.left - BUBBLE_GAP - besideWidth,
                right: d.left - BUBBLE_GAP,
                top: d.bottom - area / Math.max(besideWidth, 1),
                bottom: d.bottom,
            };
            const covers = (rect: typeof above): boolean =>
                avoid.some((a) => rect.left < a.right && rect.right > a.left && rect.top < a.bottom && rect.bottom > a.top);

            if (!covers(above)) {
                placement = 'above';
            } else if (besideWidth >= BUBBLE_MIN_BESIDE && !covers(beside)) {
                placement = 'beside';
            } else if (this.message.sticky) {
                // A tour step waits for the coach and must stay readable: above what it points at,
                // where there is room, else above the disc as usual
                const lift = spot ? above.bottom - (spot.top - BUBBLE_GAP) : 0;
                const fits = spot && above.top - lift >= SCREEN_EDGE;
                placement = fits ? 'lifted' : 'above';
                this.bubbleLift = fits ? lift : 0;
            } else {
                placement = 'parked';
            }
        }

        this.bubblePlacement = placement;
        if (placement === 'parked' && previous !== 'parked') {
            // Waits on the disc; the timer starts again once it can be read
            clearTimeout(this.bubbleTimer);
        } else if (placement !== 'parked' && previous === 'parked') {
            this.startBubbleTimer(this.message);
        }
    }

    private startBubbleTimer(message: ISidekickMessage): void {
        if (message.sticky) {
            return;
        }
        this.bubbleTimer = setTimeout(
            () => this.sidekick.closeMessage('timeout'),
            message.cta ? SIDEKICK_BUBBLE_CTA_MS : SIDEKICK_BUBBLE_MS
        );
    }

    private updateRoute(url: string): void {
        const segments = this.router.parseUrl(url).root.children['primary']?.segments ?? [];
        this.isOnHome = segments.length === 0;
        this.isOnAllowedPage = !HIDDEN_SECTIONS.includes(segments[0]?.path ?? '');
    }

    /** Keeps the body class in step and plays the intro the first time the sidekick appears in a visit. */
    private syncVisibility(): void {
        const isVisible = this.isVisible;
        this.document.body.classList.toggle(SIDEKICK_BODY_CLASS, isVisible);

        if (isVisible && !this.wasVisible) {
            this.playEntrance();
        }
        if (!isVisible) {
            this.isAwaitingStatus = false;
        }
        this.wasVisible = isVisible;
    }

    /**
     * The birth (once per coach) or this visit's intro. The birth waits for the account's status: this
     * device's cache may not know that the coach already met the co-coach on another device.
     */
    private playEntrance(): void {
        if (!this.isVisible) {
            return;
        }
        if (this.shouldOnboard() && !this.onboarding) {
            this.isAwaitingStatus = true;
            return;
        }
        if (this.shouldOnboard() && this.sidekick.takeBirth()) {
            if (this.prefersReducedMotion()) {
                // No animation: straight to its name (if it has none yet), then on to the guided session
                this.afterAlive();
            } else {
                this.startBirth();
            }
        } else if (this.sidekick.takeIntro() && !this.prefersReducedMotion()) {
            this.isScattered = true;
            setTimeout(() => (this.isScattered = false), INTRO_START_MS);
        }
    }

    private startBirth(): void {
        this.isMenuOpen = false;
        this.birthPhase = 'gather';
        this.isScattered = true;
        this.birthTimers = [
            setTimeout(() => {
                this.birthPhase = 'assemble';
                this.isScattered = false;
            }, INTRO_START_MS),
            setTimeout(() => this.cheerAtBirth(), INTRO_START_MS + SIDEKICK_BIRTH_MS.ASSEMBLE),
            setTimeout(() => this.afterAlive(), INTRO_START_MS + SIDEKICK_BIRTH_MS.ASSEMBLE + SIDEKICK_BIRTH_MS.ALIVE),
        ];
    }

    /** Alive and introduced as the coach's co-coach: it asks for its name, unless it has one. */
    private afterAlive(): void {
        if (this.coachName) {
            this.land();
            return;
        }
        this.birthPhase = 'naming';
        this.nameDraft = '';
        this.nameError = null;
        setTimeout(() => this.host.nativeElement.ownerDocument.getElementById('sidekick-birth-name')?.focus(), 0);
    }

    /** Named at birth: it says its name, then moves into its corner. */
    private introduceName(): void {
        this.birthPhase = 'named';
        this.isCheering = !this.prefersReducedMotion();
        this.cheerTimer = setTimeout(() => (this.isCheering = false), SIDEKICK_CHEER_MS);
        this.birthTimers = [setTimeout(() => this.land(), SIDEKICK_BIRTH_MS.NAMED)];
    }

    private cheerAtBirth(): void {
        this.birthPhase = 'alive';
        this.isCheering = true;
        this.cheerTimer = setTimeout(() => (this.isCheering = false), SIDEKICK_CHEER_MS);
    }

    private land(): void {
        // Landed (or skipped): the birth is done, also if the coach leaves before the landing ends
        this.sidekick.finishBirth();
        this.birthPhase = 'landing';
        this.isCheering = false;
        this.birthTimers = [
            setTimeout(() => {
                this.birthPhase = 'none';
                this.birthTimers = [];
                this.scheduleGreeting();
            }, SIDEKICK_BIRTH_MS.LANDING),
        ];
    }

    private clearBirthTimers(): void {
        this.birthTimers.forEach((timer) => clearTimeout(timer));
        this.birthTimers = [];
    }

    private prefersReducedMotion(): boolean {
        return this.document.defaultView?.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
    }

    private withIntro<T extends { introX: number; introY: number }>(
        dot: T,
        index: number
    ): T & { intro: string; birthIntro: string; birthDelay: string } {
        const far = (value: number): string => (value * BIRTH_SCATTER).toFixed(1);
        return {
            ...dot,
            intro: `translate(${dot.introX.toFixed(1)}px, ${dot.introY.toFixed(1)}px)`,
            birthIntro: `translate(${far(dot.introX)}px, ${far(dot.introY)}px)`,
            // Dots arrive one after another rather than all at once
            birthDelay: `${(index * 53) % 700}ms`,
        };
    }
}
